<template>
  <div class="chips-row">
    <template v-for="(item, index) in infoItems" :key="`info-item-${index}`">
      <div
        v-if="item?.label?.value"
        :data-id="item.type"
        class="chip"
        :class="'chip--' + item.type"
      >
        <component :is="item?.icon" class="chip-icon" />
        <span v-html="item?.label?.value"></span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import IconDuration from '~/assets/icons/event/event-info-duration.svg?component'
import IconLocation from '~/assets/icons/event/event-info-location.svg?component'
import IconLanguages from '~/assets/icons/event/event-info-lang.svg?component'

import type { EventDescription } from '~/composables/types/event'

const { eventData = null, isGiftcard = false } = defineProps<{
  eventData?: EventDescription | null
  isGiftcard?: boolean
}>()

const { t: $t } = useI18n()

const locationLabel = computed(() => {
  if (eventData?.type === 'ONLINE') return 'Online'

  if (eventData?.has_mobile_classes)
    return $t('product.show.infoBanner.atYourLocation')

  if (eventGetters.getHasMultipleLocations(eventData as EventDescription))
    return $t('product.show.giftcardIcons.multipleLocationsSummary')

  return eventData?.address?.data?.locality?.data?.name
})

const durationLabel = computed(() => {
  if (isGiftcard) return $t('product.show.giftcardIcons.durationContent')

  const multiDateDuration =
    eventGetters.getMultiDateDurationPerSession(eventData)
  const durationInHumanReadableString = multiDateDuration
    ? `${convertMinutesToHumanString(multiDateDuration)}  ${$t('product.show.importantDetails.perSession')}`
    : convertMinutesToHumanString(
        +eventGetters.getNextDateAvailableDuration(
          eventData as EventDescription | undefined
        ) ||
          +eventGetters.getDefaultDuration(
            eventData as EventDescription | undefined
          ) ||
          +eventGetters.getEventMinimumDuration(
            eventData as EventDescription | undefined
          ) ||
          +eventGetters.getEventMaximumDuration(
            eventData as EventDescription | undefined
          )
      )
  return (
    '<span class="hidden md:inline-block">' +
    $t('product.show.infoBanner.duration') +
    '</span>' +
    ' ' +
    durationInHumanReadableString
  )
})

const languagesLabel = computed(() => {
  return isGiftcard
    ? $t('product.show.giftcardIcons.languages.content')
    : eventGetters
        .getLanguages(eventData as EventDescription)
        ?.map((i) => $t(`product.show.icons.languages.list.${i}`))
        .join(', ')
})

const infoItems = computed(() => {
  return [
    {
      type: 'duration',
      icon: IconDuration,
      label: durationLabel,
    },
    {
      type: 'location',
      icon: IconLocation,
      label: locationLabel,
    },
    {
      type: 'languages',
      icon: IconLanguages,
      label: languagesLabel,
    },
  ]
})
</script>

<style lang="postcss" scoped>
.chips-row {
  @apply flex w-full flex-wrap items-center gap-2 my-4 md:my-5;
}

.chip {
  @apply inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full  border-teal-300 bg-teal-50 px-4 py-1.5 text-left text-sm font-normal leading-none text-stone-900 antialiased;
  @apply max-w-full;
}

.chip:focus-visible {
  @apply outline outline-2 outline-offset-2 outline-stone-900;
}

.chip--people,
.chip--duration {
  @apply whitespace-nowrap;
}

.chip-icon {
  @apply size-5 min-w-5 shrink-0 text-stone-900;
}
</style>
